import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useAdminReports, useUpdateReport, useFreezeUser, useBanUser, useWarnUser } from '../api/useAdmin'
import { useDeleteComment } from '../api/useComments'
import { formatDate } from '../utils/date'
import Pagination from '../components/ui/Pagination'
import toast from 'react-hot-toast'

const STATUS_LABEL = { new: 'Новая', reviewed: 'Рассмотрена', closed: 'Закрыта' }
const STATUS_CSS = { new: 'text-amber-400 bg-amber-400/10', reviewed: 'text-blue-400 bg-blue-400/10', closed: 'text-muted bg-surface2' }

export default function AdminReports() {
  const [page, setPage] = useState(1)
  const [statusFilter, setStatusFilter] = useState('')
  const { data, isLoading } = useAdminReports(page, statusFilter)
  const { mutate: updateReport } = useUpdateReport()
  const { mutate: deleteComment } = useDeleteComment()
  const { mutate: warnUser } = useWarnUser()
  const { mutate: freezeUser } = useFreezeUser()
  const { mutate: banUser } = useBanUser()

  const reports = data?.items || []
  const pages = data?.pages || 1
  const total = data?.total || 0

  const handleAction = (report, action) => {
    const commentAuthorId = report.comment?.user_id
    switch (action) {
      case 'delete':
        if (!report.comment_id) { toast.error('Комментарий уже удалён'); return }
        deleteComment(report.comment_id, {
          onSuccess: () => {
            toast.success('Комментарий удалён')
            updateReport({ reportId: report.id, status: 'reviewed' })
          },
          onError: () => toast.error('Ошибка при удалении'),
        })
        break
      case 'warn':
        if (!commentAuthorId) { toast.error('Пользователь не найден'); return }
        warnUser({ userId: commentAuthorId, reason: `Жалоба на комментарий: ${report.reason}` }, {
          onSuccess: () => {
            toast.success('Предупреждение отправлено')
            updateReport({ reportId: report.id, status: 'reviewed' })
          },
          onError: () => toast.error('Ошибка'),
        })
        break
      case 'freeze':
        if (!commentAuthorId) { toast.error('Пользователь не найден'); return }
        freezeUser({ userId: commentAuthorId, reason: report.reason }, {
          onSuccess: () => {
            toast.success('Аккаунт заморожен')
            updateReport({ reportId: report.id, status: 'reviewed' })
          },
          onError: () => toast.error('Ошибка'),
        })
        break
      case 'ban':
        if (!commentAuthorId) { toast.error('Пользователь не найден'); return }
        banUser({ userId: commentAuthorId, reason: report.reason }, {
          onSuccess: () => {
            toast.success('Аккаунт заблокирован')
            updateReport({ reportId: report.id, status: 'reviewed' })
          },
          onError: () => toast.error('Ошибка'),
        })
        break
      case 'close':
        updateReport({ reportId: report.id, status: 'closed' }, {
          onSuccess: () => toast.success('Жалоба закрыта'),
          onError: () => toast.error('Ошибка'),
        })
        break
    }
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
      <div className="flex items-center gap-3 mb-6">
        <Link to="/admin" className="text-sm text-muted hover:text-accent transition-colors">← Админ</Link>
        <span className="text-line">/</span>
        <h1 className="text-xl font-bold">Жалобы</h1>
        {total > 0 && <span className="text-sm text-muted">{total}</span>}
      </div>

      <div className="flex gap-2 mb-5">
        {['', 'new', 'reviewed', 'closed'].map((s) => (
          <button key={s} onClick={() => { setStatusFilter(s); setPage(1) }}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${statusFilter === s ? 'bg-accent text-on-accent' : 'bg-surface text-muted hover:text-fg'}`}>
            {s === '' ? 'Все' : STATUS_LABEL[s]}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="flex flex-col gap-3">
          {[1, 2, 3].map((i) => <div key={i} className="h-28 rounded-xl animate-pulse bg-surface" />)}
        </div>
      ) : reports.length === 0 ? (
        <p className="text-center py-16 text-muted">Жалоб нет</p>
      ) : (
        <>
          <div className="flex flex-col gap-3">
            {reports.map((r) => (
              <div key={r.id} className="p-4 rounded-xl bg-surface border border-line">
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-semibold">{r.reporter?.username || 'Аноним'}</span>
                      <span className="text-xs text-muted">→ жалоба на</span>
                      {r.comment ? (
                        <span className="text-sm font-semibold text-accent">
                          комментарий от {r.comment.user_id ? <span className="text-fg">пользователя</span> : 'удалённого'}
                        </span>
                      ) : (
                        <span className="text-sm text-muted italic">комментарий удалён</span>
                      )}
                    </div>
                    <p className="text-xs text-muted">{formatDate(r.created_at)}</p>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded-full shrink-0 font-medium ${STATUS_CSS[r.status] || STATUS_CSS.closed}`}>
                    {STATUS_LABEL[r.status] || r.status}
                  </span>
                </div>

                <div className="mb-3 p-3 rounded-lg bg-surface2">
                  <p className="text-xs font-semibold text-muted mb-1">Причина жалобы</p>
                  <p className="text-sm text-fg">{r.reason}</p>
                </div>

                {r.comment && (
                  <div className="mb-3 p-3 rounded-lg bg-surface2 border-l-2 border-accent/40">
                    <p className="text-xs font-semibold text-muted mb-1">Текст комментария</p>
                    <p className="text-sm text-fg">{r.comment.text}</p>
                  </div>
                )}

                {r.status === 'new' && (
                  <div className="flex flex-wrap gap-2">
                    <button onClick={() => handleAction(r, 'delete')}
                      className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-red-400/30 text-red-400 hover:bg-red-400/10 transition-colors">
                      Удалить комментарий
                    </button>
                    <button onClick={() => handleAction(r, 'warn')}
                      className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-amber-400/30 text-amber-400 hover:bg-amber-400/10 transition-colors">
                      Предупредить
                    </button>
                    <button onClick={() => handleAction(r, 'freeze')}
                      className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-blue-400/30 text-blue-400 hover:bg-blue-400/10 transition-colors">
                      Заморозить
                    </button>
                    <button onClick={() => handleAction(r, 'ban')}
                      className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-red-600/30 text-red-500 hover:bg-red-500/10 transition-colors">
                      Забанить
                    </button>
                    <button onClick={() => handleAction(r, 'close')}
                      className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-line text-muted hover:text-fg transition-colors ml-auto">
                      Закрыть
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>

          <Pagination page={page} total={pages} onChange={setPage} />
        </>
      )}
    </div>
  )
}
